import {
	Alert,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	FormLabel,
	Grid,
	IconButton,
	Switch,
	SxProps,
	Theme,
	Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box } from "@mui/system";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import en from "date-fns/locale/en-GB";
import CheckboxesTags from "../../../common/components/CheckboxesTags";
import CustomDatePicker from "../../../common/components/CustomDatePicker";
import CustomInput from "../../../common/components/CustomInput";
import {
	CHAPTER51_NUMBER_OF_HOURS,
	CLICK_GRANULARITY,
	NEGATIVE_PRICE_OPERATOR_LIST,
	PRICING_GRANULARITY,
} from "../PricingRequestStepper.constant";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { lightGrey, warningColor } from "../../../core/theme";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
	selectCommodityFixingsState,
	selectPricingAddOnsState,
	selectPricingIndexesState,
	selectProvisionTypesState,
	selectSpotMarketState,
} from "../PricingRequestStepper.selector";
import { CommodityFixings } from "../PricingRequestStepper.slice";
import CustomSelect from "../../../common/components/CustomSelect";
import { endOfYear } from "date-fns";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { ClickabilityGranularity } from "../pricingRequestStepper.schema";
import _ from "lodash";
import { useTemporaryFetch } from "../../../common/hooks/useTemporaryFetch";
import { Tender } from "../../tender_page/tender.module";
import {
	PricingRequestData,
	PricingRequestDataKeys,
} from "../pricingRequestStepper.module";
import PricingTemplateInput from "./PricingTemplateSelect";
import {
	FeatureName,
	useFeatureFlags,
} from "../../../common/hooks/useFeatureFlags";
import Feature from "../../../common/components/Feature";
import { Portfolio, Site } from "../../sites/sites.module";
import { allSameCountry } from "../utils/helpers.utils";
import { If } from "../../../common/components/If";
import PricingTypeError from "./PricingTypeError";
import { SupportedCountry } from "../../../common/constants/countries";
import CapFloorCard from "./CapFloorCard";
import { getCurrencySymbol } from "../../../common/constants/currency";
import { useTranslation } from "react-i18next";
import { useStepConfiguration } from "../hooks/useStepConfiguration";
import { useDefaultProvisionType } from "../hooks/useDefaultProvisionType";
import UsSpecificsSelectors from "./UsSpecificsSelectors";
import FluidSelectMultiple from "../../../common/components/FluidSelectMulti";

const style: { [key: string]: SxProps<Theme> } = {
	alignedProupe: {
		flexDirection: "row",
		textTransform: "capitalize",
		marginBottom: "5px",
	},
	titlePricingType: {
		marginBottom: "32px",
		fontWeight: 700,
	},
	titleOptions: {
		fontWeight: 700,
	},
	datePicker: {
		width: "100%",
		"& p": {
			backgroundColor: lightGrey,
		},
	},
	adjustmentPower: {
		width: "200px",
		"& p": {
			backgroundColor: lightGrey,
		},
	},
	granularityCheckBox: {
		flexDirection: "column",
		textTransform: "capitalize",
	},
	clickCount: {
		width: "180px",
		marginBottom: "1em",
		"& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
			{
				WebkitAppearance: "none",
				margin: 0,
			},
		"& input": {
			textAlign: "center",
		},
		"& input::placeholder": {
			textAlign: "center",
		},
		"& svg": {
			color: "grey",
		},
	},
	pricingDate: {
		width: "100%",
	},
};

interface PricingStepProps {
	values: PricingRequestData;
	handleChange: any;
	setFieldValue: (fieldName: string, value: any) => void;
	errors: any;
	touched: any;
	tenderId?: number;
	sitesValues: (Site | Portfolio)[];
}

function PricingStep(props: PricingStepProps) {
	const {
		values,
		handleChange,
		setFieldValue,
		errors,
		touched,
		tenderId,
		sitesValues,
	} = props;
	const allSitesHasSameCountry = allSameCountry(sitesValues);

	const { isFeature } = useFeatureFlags();
	const { t } = useTranslation();
	const commodityFixingsList = useSelector(selectCommodityFixingsState);
	const pricingIndexes = useSelector(selectPricingIndexesState);
	const provisionTypesList = useSelector(selectProvisionTypesState);
	const addonsList = useSelector(selectPricingAddOnsState);

	const spotMarketsList = useSelector(selectSpotMarketState);
	const { data: tender, fetchTemporaryData: fetchTender } =
		useTemporaryFetch<Tender>((response) => response.data);

	const [resultOptionsDisabled, setResultOptionsDisabled] = useState(true);
	const [baseProdDisabled, setBaseProdDisabled] = useState(true);

	useEffect(() => {
		if (tenderId && !tender) {
			fetchTender("/tenders/" + tenderId);
		}
	}, [tender, tenderId]);

	useEffect(() => {
		if (tender && !values.hasNegativePrice) {
			setFieldValue(
				"hasNegativePrice",
				!!tender?.has_negative_price_clause
			);
		}
	}, [tender]);

	useEffect(() => {
		setFieldValue("spotMarket", "NONE");
	}, []);

	useDefaultProvisionType(setFieldValue, tender);
	useEffect(() => {
		if (isFeature(FeatureName.pricing_stepper_negative_price, true)) {
			setFieldValue("negativePriceIsActive", true);
		}

		if (
			isFeature(FeatureName.pricing_stepper_curtailability_forward, true)
		) {
			setFieldValue("curtailabilityForwardIsActive", true);
		}
	}, [isFeature]);

	useEffect(() => {
		const indexes = values?.pricingType ?? [];
		const hasIndexPrice =
			indexes.findIndex((index) => index.has_result_options) >= 0;

		const isBaseProdIndex =
			indexes.findIndex((index) => !!index.is_baseprod) >= 0;

		setFieldValue("baseOptionSwitch", isBaseProdIndex);

		setBaseProdDisabled(!isBaseProdIndex);
		setResultOptionsDisabled(!hasIndexPrice);
	}, [values.pricingType]);

	const stepConfig = useStepConfiguration(values.pricingType);

	useEffect(() => {
		// Deactivate switch for which the pricing type does not require each section
		// Otherwise formik is going to enforce that section is filled even if not displayed
		// Which can lead to being stuck without understanding why
		if (!stepConfig.show_pricing_section_base_option) {
			setFieldValue("baseOptionSwitch", false);
		}
		if (!stepConfig.show_pricing_section_cap_floor) {
			setFieldValue("hasPayoutMethod", false);
		}
		if (!stepConfig.show_pricing_section_cap_floor_intraday) {
			setFieldValue("hasPayoutMethodIntraDay", false);
		}
		if (!stepConfig.show_pricing_section_chapter_51) {
			setFieldValue("chapter51Switch", false);
		}
		if (!stepConfig.show_pricing_section_clickability) {
			setFieldValue("clickOptionSwitch", false);
		}
		if (!stepConfig.show_pricing_section_curtailability_forward) {
			setFieldValue("hasCurtailabilityForward", false);
		}
		if (!stepConfig.show_pricing_section_negative_price) {
			setFieldValue("hasNegativePrice", false);
		}
		if (!stepConfig.show_pricing_section_result_options) {
			setFieldValue("alpha", false);
			setFieldValue("beta", false);
			setFieldValue("alphaAndBeta", false);
		}
		if (!stepConfig.show_pricing_section_strike_price) {
			setFieldValue("strikePriceSwitch", false);
		}
		if (!stepConfig.show_pricing_section_addons) {
			setFieldValue("pricing_add_ons", []);
		}
		if (!stepConfig.show_pricing_section_power_goo) {
			setFieldValue("ppaWithGooSwitch", false);
		}
	}, [values, setFieldValue, stepConfig]);

	useEffect(() => {
		if (!resultOptionsDisabled) {
			setFieldValue("alpha", true);
		} else {
			setFieldValue("alpha", false);
			setFieldValue("beta", false);
		}
	}, [resultOptionsDisabled]);

	useEffect(() => {
		if ((stepConfig.available_ppa_types?.length || 0) <= 1) {
			setFieldValue("ppaShapeType", stepConfig.available_ppa_types);
		}
	}, [stepConfig.available_ppa_types]);

	const chapter51Values = useMemo(
		() =>
			_.intersection(
				...(values.pricingType || []).map(
					(type) => type.chapter51_options || []
				)
			),
		[values]
	);

	const allowChapter51 = useMemo(
		() => !!chapter51Values.length,
		[chapter51Values]
	);

	const allowClick = useMemo(
		() =>
			!_.some(
				values.pricingType?.map((value) => !value.has_clickability)
			),
		[values]
	);

	const onPricingTypeChanges = useCallback(
		(fieldName: string, fieldValues: CommodityFixings[]) => {
			setFieldValue(fieldName, fieldValues);
			if (fieldValues.some((val) => val.name === "Fix")) {
				setFieldValue("hasPayoutMethod", false);
				setFieldValue("hasFixPrice", true);
				setFieldValue("payoutMethod", undefined);
				setFieldValue("cap", "");
				setFieldValue("floor", "");
				setFieldValue("hasPayoutMethodIntraDay", false);
				setFieldValue("payoutMethodIntraDay", undefined);
				setFieldValue("capIntraDay", "");
				setFieldValue("floorIntraDay", "");
			} else {
				setFieldValue("hasFixPrice", false);
			}
			const newChapter51Values = _.intersection(
				...fieldValues.map((type) => type.chapter51_options || [])
			);

			const newAllowChapter51 = !!newChapter51Values.length;

			// if with the new pricing type we cannot find at least
			// on chapter51 option we disable the feature
			if (!newAllowChapter51) {
				setFieldValue("chapter51Switch", false);
				setFieldValue("chapter51Type", "");
				setFieldValue("chapter51NumberOfHours", "");
			}
			// reset chapter 51 type if new pricing type selection does not allow previous value
			if (
				values.chapter51Type &&
				!newChapter51Values.includes(values.chapter51Type)
			) {
				setFieldValue("chapter51Type", "");
			}
			if (_.some(fieldValues.map((value) => !value.has_clickability))) {
				setFieldValue("clickOptionSwitch", false);
			}
		},
		[setFieldValue, values]
	);

	return (
		<Grid container columnSpacing={4} rowSpacing={2}>
			<Grid item xs={12} display="flex" justifyContent="flex-start">
				<Typography
					variant="h6"
					component="h6"
					sx={style.titlePricingType}
				>
					Power pricing type
				</Typography>
			</Grid>

			<Grid item xs={12}>
				<PricingTemplateInput
					value={values.template}
					setFieldValue={setFieldValue}
					commodityFixingsList={commodityFixingsList}
				/>
			</Grid>
			<Grid item xs={12}>
				<Box component="span">
					{Array.isArray(values.pricingType) && (
						<CheckboxesTags<CommodityFixings>
							label="Pricing type"
							trackBy="name"
							name="pricingType"
							value={values.pricingType}
							options={commodityFixingsList}
							setFieldValue={onPricingTypeChanges}
							error={!!errors.pricingType && touched.pricingType}
							helperText={
								!!errors.pricingType && touched.pricingType
									? errors.pricingType ||
									  "Please select at least one pricing type"
									: ""
							}
						/>
					)}
					{commodityFixingsList?.length === 0 && (
						<PricingTypeError text="No pricing type is assigned to the selected country." />
					)}
				</Box>
			</Grid>

			<Grid item xs={6}>
				<LocalizationProvider
					dateAdapter={AdapterDateFns}
					adapterLocale={en}
				>
					<CustomDatePicker
						format={"MM/yyyy"}
						name="startDate"
						sx={style.datePicker}
						views={["month", "year"]}
						onChange={(value) => {
							if (value) {
								const startDate = new Date(value as string);
								const endDate = endOfYear(startDate);
								setFieldValue("endDate", endDate);
							}
						}}
						slotProps={{
							textField: {
								size: "small",
								label: "Start date (month/year)",
								error:
									!!errors.startDate && !!touched.startDate,
								helperText:
									!!errors.startDate && touched.startDate
										? errors.startDate
										: "",
							},
						}}
					/>
				</LocalizationProvider>
			</Grid>
			<Grid item xs={6}>
				<LocalizationProvider
					dateAdapter={AdapterDateFns}
					adapterLocale={en}
				>
					<CustomDatePicker
						format={"MM/yyyy"}
						name="endDate"
						minDate={values.startDate}
						sx={style.datePicker}
						views={["month", "year"]}
						lastDayOfTheMonth
						slotProps={{
							textField: {
								size: "small",
								label: "End date (month/year)",
								error: !!errors.endDate && !!touched.endDate,
								helperText:
									!!errors.endDate && touched.endDate
										? errors.endDate
										: "",
							},
						}}
						onChange={(value) => {
							setFieldValue("gooEndDate", value);
						}}
					/>
				</LocalizationProvider>
			</Grid>

			<Grid item xs={12} display="flex" justifyContent="flex-start">
				<FormGroup sx={style.granularityCheckBox}>
					<FormLabel sx={{ display: "flex" }}>Granularity</FormLabel>
					<Box>
						<FormControlLabel
							name="granularityMonthly"
							control={
								<Checkbox
									onChange={handleChange}
									checked={values.granularityMonthly}
								/>
							}
							label={PRICING_GRANULARITY[0]}
						/>
						<FormControlLabel
							name="granularityQuarter"
							control={
								<Checkbox
									onChange={handleChange}
									checked={values.granularityQuarter}
								/>
							}
							label={PRICING_GRANULARITY[1]}
						/>
						<FormControlLabel
							name="granularityCal"
							control={
								<Checkbox
									onChange={handleChange}
									checked={values.granularityCal}
								/>
							}
							label={PRICING_GRANULARITY[2]}
						/>
						<FormControlLabel
							name="granularityWholeHorizon"
							control={
								<Checkbox
									onChange={handleChange}
									checked={values.granularityWholeHorizon}
								/>
							}
							label={PRICING_GRANULARITY[3]}
						/>
					</Box>
				</FormGroup>
			</Grid>
			<Feature
				name={FeatureName.pricing_stepper_cross_date}
				is_active={true}
			>
				<Grid item xs={6} display="flex" justifyContent="flex-start">
					<FormGroup sx={style.pricingDate}>
						<FormLabel sx={{ display: "flex" }}>
							Cross Date
						</FormLabel>
						<Box sx={{ marginTop: "10px" }}>
							<LocalizationProvider
								dateAdapter={AdapterDateFns}
								adapterLocale={en}
							>
								<CustomDatePicker
									disabled={values.live}
									name="pricingDate"
									sx={style.datePicker}
									maxDate={new Date()}
									slotProps={{
										textField: {
											size: "small",
											error:
												!!errors.pricingDate &&
												!!touched.pricingDate,
											helperText:
												!!errors.pricingDate &&
												touched.pricingDate
													? errors.pricingDate
													: "",
										},
									}}
								/>
							</LocalizationProvider>
						</Box>
					</FormGroup>
				</Grid>
			</Feature>
			<If condition={stepConfig.show_pricing_section_result_options}>
				<Grid
					item
					xs={6}
					display="flex"
					flexDirection={"column"}
					justifyContent="flex-start"
				>
					<FormGroup sx={style.pricingDate}>
						<FormLabel sx={{ display: "flex" }}>
							Result options
						</FormLabel>
						<Box>
							<FormControlLabel
								name="alpha"
								disabled={resultOptionsDisabled}
								control={
									<Checkbox
										onChange={handleChange}
										checked={values.alpha}
									/>
								}
								label="Alpha"
							/>
							<FormControlLabel
								name="beta"
								disabled={resultOptionsDisabled}
								control={
									<Checkbox
										onChange={handleChange}
										checked={values.beta}
									/>
								}
								label="Beta"
							/>
							<FormControlLabel
								name="alphaAndBeta"
								disabled={resultOptionsDisabled}
								control={
									<Checkbox
										onChange={handleChange}
										checked={values.alphaAndBeta}
									/>
								}
								label="Alpha + Beta"
							/>
						</Box>
						{errors.alpha && (
							<FormHelperText
								sx={{ color: "red", fontSize: "16px" }}
							>
								{errors.alpha}
							</FormHelperText>
						)}
					</FormGroup>
				</Grid>
			</If>
			<Grid item xs={10} display="flex" justifyContent="flex-start">
				<Typography variant="h6" component="h6" sx={style.titleOptions}>
					Parameters
				</Typography>
			</Grid>

			<If
				condition={
					stepConfig.show_pricing_section_base_option ||
					stepConfig.show_pricing_section_chapter_51
				}
			>
				<Grid
					item
					xs={3}
					display="flex"
					rowSpacing={2}
					flexDirection={"column"}
					justifyContent="flex-start"
				>
					<If condition={stepConfig.show_pricing_section_base_option}>
						<FormGroup>
							<FormLabel
								sx={{ display: "flex", marginTop: "5px" }}
							>
								Base Options
							</FormLabel>
						</FormGroup>

						<CustomInput
							data-testid="base-capacity-volume"
							name="baseCapacity"
							type="number"
							disabled={baseProdDisabled}
							value={values.baseCapacity}
							onChange={handleChange}
							sx={{ width: "60%", marginTop: "5px" }}
							errorText={errors.baseCapacity}
							touched={touched.baseCapacity}
							placeholder="0"
							label="Base Capacity Volume (%P50)"
						></CustomInput>
					</If>
					<If condition={stepConfig.show_pricing_section_chapter_51}>
						<Box sx={{ marginTop: "1em" }}>
							<FormGroup>
								<FormControlLabel
									control={
										<Switch
											name={"chapter51Switch"}
											checked={values.chapter51Switch}
											disabled={!allowChapter51}
											onChange={(event) => {
												handleChange(event);
												setFieldValue(
													"chapter51Type",
													""
												);
												setFieldValue(
													"chapter51NumberOfHours",
													""
												);
											}}
										/>
									}
									label="Chapter51 Options"
								/>
							</FormGroup>

							<CustomSelect
								datatestId="chapter51Type"
								name="Chapter51 Type"
								label="Chapter51 Type"
								value={values.chapter51Type}
								disabled={!values.chapter51Switch}
								onChange={(event) =>
									setFieldValue(
										"chapter51Type",
										event.target.value
									)
								}
								error={!!errors.chapter51Type}
								errorText={errors.chapter51Type}
								touched={!!touched.chapter51Type}
								sx={{
									width: "250px",
								}}
								sxFC={{
									marginTop: "5px",
								}}
								items={chapter51Values.map((item) => {
									return {
										key: item,
										value: item,
									};
								})}
							></CustomSelect>
							<CustomSelect
								datatestId="chapter51NumberOfHours"
								name="Chapter51 Number Of Hours"
								label="Chapter51 Number Of Hours"
								value={values.chapter51NumberOfHours}
								disabled={!values.chapter51Switch}
								error={!!errors.chapter51NumberOfHours}
								errorText={errors.chapter51NumberOfHours}
								touched={!!touched.chapter51NumberOfHours}
								onChange={(event) =>
									setFieldValue(
										"chapter51NumberOfHours",
										event.target.value
									)
								}
								sxFC={{
									width: "250px",
									marginTop: "1em",
								}}
								items={CHAPTER51_NUMBER_OF_HOURS.map((item) => {
									return {
										key: item,
										value: item,
									};
								})}
							></CustomSelect>
						</Box>
					</If>
				</Grid>
			</If>
			<If condition={stepConfig.show_pricing_section_negative_price}>
				<Grid
					item
					xs={3}
					display="flex"
					flexDirection={"column"}
					justifyContent="flex-start"
				>
					<FormGroup>
						<FormControlLabel
							control={
								<Switch
									name={"hasNegativePrice"}
									checked={values.hasNegativePrice}
									onChange={handleChange}
								/>
							}
							label={t("pricing.stepper.negative_price")}
						/>
					</FormGroup>
					<FormControl>
						<FormLabel>Value</FormLabel>
						<CustomInput
							name="negativePriceValue"
							disabled={!values.hasNegativePrice}
							onChange={handleChange}
							variant="standard"
							type="number"
							value={values.negativePriceValue}
							error={!!errors.negativePriceValue}
							errorText={t(errors.negativePriceValue)}
							touched={true}
						/>
					</FormControl>
					<Feature
						name={FeatureName.pricing_stepper_negative_price}
						is_active={true}
					>
						<CustomSelect
							datatestId="negativePriceIndex"
							name="negativePriceIndex"
							label="Index"
							value={values.negativePriceIndex?.geco_id}
							disabled={!values.hasNegativePrice}
							onChange={(e) => {
								setFieldValue(
									"negativePriceIndex",
									pricingIndexes.find(
										(pi) => pi.geco_id === e.target.value
									)
								);
							}}
							error={!!errors.negativePriceIndex}
							errorText={t(errors.negativePriceIndex)}
							touched={true}
							items={pricingIndexes?.map((el) => ({
								key: el.geco_id,
								value: el.geco_id,
								label: el.name,
							}))}
						/>
						<If
							condition={
								values.hasNegativePrice &&
								!allSitesHasSameCountry
							}
						>
							<Box display={"flex"} color={warningColor}>
								<WarningAmberIcon /> sites with different
								countries were selected
							</Box>
						</If>

						<FormControl>
							<FormLabel>
								Threshold {getCurrencySymbol(tender?.currency)}
								/mwh
							</FormLabel>
							<CustomInput
								name="negativePriceThreshold"
								placeholder={
									getCurrencySymbol(tender?.currency) + "/mwh"
								}
								disabled={!values.hasNegativePrice}
								onChange={handleChange}
								variant="standard"
								type="number"
								value={values.negativePriceThreshold}
								error={!!errors.negativePriceThreshold}
								errorText={t(errors.negativePriceThreshold)}
								touched={true}
							/>
						</FormControl>
						<CustomSelect
							datatestId="negativePriceOperator"
							name="negativePriceOperator"
							label="Operator"
							value={values.negativePriceOperator}
							disabled={!values.hasNegativePrice}
							onChange={(event) =>
								setFieldValue(
									"negativePriceOperator",
									event.target.value
								)
							}
							error={!!errors.negativePriceOperator}
							errorText={t(errors.negativePriceOperator)}
							touched={true}
							items={NEGATIVE_PRICE_OPERATOR_LIST}
						/>
						<FormControl>
							<CustomInput
								name="negativePriceNBConsecutiveBlocks"
								placeholder="NB of Consecutive blocks"
								disabled={!values.hasNegativePrice}
								minRows={1}
								onChange={handleChange}
								variant="standard"
								type="number"
								value={values.negativePriceNBConsecutiveBlocks}
								error={
									!!errors.negativePriceNBConsecutiveBlocks
								}
								errorText={t(
									errors.negativePriceNBConsecutiveBlocks
								)}
								touched={true}
							/>
						</FormControl>
					</Feature>
				</Grid>
			</If>
			<Grid
				item
				xs={6}
				display="flex"
				flexDirection={"column"}
				justifyContent="flex-start"
			>
				<If condition={stepConfig.show_pricing_section_power_goo}>
					<>
						<FormGroup>
							<FormControlLabel
								control={
									<Switch
										name={"ppaWithGooSwitch"}
										checked={values.ppaWithGooSwitch}
										onChange={handleChange}
									/>
								}
								label={t("pricing.stepper.add_goo")}
							/>
						</FormGroup>
						<FormControl>
							<FormLabel>
								{t("pricing.stepper.goo_adjustment_on_power")}
							</FormLabel>
							<CustomInput
								name="gooPowerAdjustment"
								placeholder="%"
								disabled={!values.ppaWithGooSwitch}
								sx={style.adjustmentPower}
								onChange={handleChange}
								variant="standard"
								type="number"
								value={values.gooPowerAdjustment}
								error={!!errors.gooPowerAdjustment}
								errorText={errors.gooPowerAdjustment}
								touched={true}
							/>
						</FormControl>
					</>
				</If>
				<If condition={stepConfig.show_pricing_section_clickability}>
					<FormGroup>
						<FormControlLabel
							control={
								<Switch
									name={"clickOptionSwitch"}
									disabled={!allowClick}
									checked={values.clickOptionSwitch}
									onChange={handleChange}
								/>
							}
							label="Clickability Options"
						/>
					</FormGroup>
					<FormControl error={true}>
						<FormGroup sx={style.alignedProupe}>
							{CLICK_GRANULARITY.map((granularity) => (
								<FormControlLabel
									key={`checkbox-${granularity}`}
									value={granularity}
									name="clickGranularity"
									onClick={() =>
										setFieldValue(
											`clickCount${granularity}`,
											1
										)
									}
									control={
										<Checkbox
											onChange={handleChange}
											disabled={!values.clickOptionSwitch}
											checked={values.clickGranularity?.includes(
												granularity as ClickabilityGranularity
											)}
										/>
									}
									label={
										granularity == "NoSplit" ? (
											<>
												<Box
													sx={{
														display: "flex",
														marginTop: "6px",
													}}
												>
													No split
													<Typography title="NoSplit means non-standard date, e.g, from June to June.">
														<HelpOutlineIcon
															sx={{
																marginLeft:
																	"5px",
															}}
														/>
													</Typography>
												</Box>
											</>
										) : (
											granularity
										)
									}
								/>
							))}

							{!!errors.clickGranularity &&
								!!touched.clickGranularity && (
									<FormHelperText>
										{errors.clickGranularity}
									</FormHelperText>
								)}
						</FormGroup>
					</FormControl>
					{Array.isArray(values.clickGranularity) &&
						values.clickGranularity.length > 0 &&
						values.clickGranularity.map((granularity: string) => (
							<CustomInput
								key={granularity}
								id={`clickCount${granularity}`}
								name={`clickCount${granularity}`}
								type="number"
								value={
									values[
										("clickCount" +
											granularity) as PricingRequestDataKeys
									]
								}
								onChange={handleChange}
								sx={style.clickCount}
								placeholder="1"
								label={"Number of clicks per " + granularity}
								disabled={!values.clickOptionSwitch}
								errorText={
									errors[
										`clickCount${granularity}` as PricingRequestDataKeys
									]
								}
								touched={
									touched[
										`clickCount${granularity}` as PricingRequestDataKeys
									]
								}
								InputProps={{
									startAdornment: (
										<IconButton
											disabled={!values.clickOptionSwitch}
											onClick={() => {
												setFieldValue(
													"clickCount" + granularity,
													Number(
														values[
															("clickCount" +
																granularity) as PricingRequestDataKeys
														]
													) > 1
														? Number(
																values[
																	("clickCount" +
																		granularity) as PricingRequestDataKeys
																]
														  ) - 1
														: 1
												);
												return (
													document.getElementById(
														`clickCount${granularity}`
													) as any
												).stepDown();
											}}
										>
											<RemoveIcon />
										</IconButton>
									),
									endAdornment: (
										<IconButton
											disabled={!values.clickOptionSwitch}
											onClick={() => {
												setFieldValue(
													"clickCount" + granularity,
													Number(
														values[
															("clickCount" +
																granularity) as PricingRequestDataKeys
														]
													) + 1
												);
												return (
													document.getElementById(
														"clickCount" +
															granularity
													) as any
												).stepUp();
											}}
										>
											<AddIcon />
										</IconButton>
									),
								}}
							></CustomInput>
						))}
				</If>
			</Grid>
			<Feature
				name={FeatureName.pricing_stepper_curtailability_forward}
				is_active={true}
			>
				<If
					condition={
						stepConfig.show_pricing_section_curtailability_forward
					}
				>
					<Grid
						item
						xs={3}
						display="flex"
						flexDirection={"column"}
						justifyContent="flex-start"
					>
						<FormGroup>
							<FormControlLabel
								control={
									<Switch
										name={"hasCurtailabilityForward"}
										checked={
											values.hasCurtailabilityForward
										}
										onChange={handleChange}
									/>
								}
								label="Curtailability forward"
							/>
						</FormGroup>
						<CustomSelect
							datatestId="curtailabilityForwardIndex"
							name="curtailabilityForwardIndex"
							label="Index"
							value={
								values.curtailabilityForwardIndex?.geco_id || ""
							}
							disabled={!values.hasCurtailabilityForward}
							onChange={(e) => {
								setFieldValue(
									"curtailabilityForwardIndex",
									pricingIndexes.find(
										(pi) => pi.geco_id === e.target.value
									)
								);
							}}
							error={!!errors.curtailabilityForwardIndex}
							errorText={errors.curtailabilityForwardIndex}
							touched={true}
							items={pricingIndexes?.map((el) => ({
								key: el.geco_id,
								value: el.geco_id,
								label: el.name,
							}))}
						/>
						<If
							condition={
								values.hasCurtailabilityForward &&
								!allSitesHasSameCountry
							}
						>
							<Box display={"flex"} color={warningColor}>
								<WarningAmberIcon /> sites with different
								countries were selected
							</Box>
						</If>

						<FormControl>
							<FormLabel>Threshold eur/kwh</FormLabel>
							<CustomInput
								name="curtailabilityForwardThreshold"
								placeholder="eur/kwh"
								disabled={!values.hasCurtailabilityForward}
								onChange={handleChange}
								variant="standard"
								type="number"
								value={values.curtailabilityForwardThreshold}
								error={!!errors.curtailabilityForwardThreshold}
								errorText={
									errors.curtailabilityForwardThreshold
								}
								touched={true}
							/>
						</FormControl>
						<CustomSelect
							datatestId="curtailabilityForwardOperator"
							name="curtailabilityForwardOperator"
							label="Operator"
							value={values.curtailabilityForwardOperator || ""}
							disabled={!values.hasCurtailabilityForward}
							onChange={(event) =>
								setFieldValue(
									"curtailabilityForwardOperator",
									event.target.value
								)
							}
							error={!!errors.curtailabilityForwardOperator}
							errorText={errors.curtailabilityForwardOperator}
							touched={true}
							items={NEGATIVE_PRICE_OPERATOR_LIST}
						/>
						<FormControl>
							<CustomInput
								name="curtailabilityForwardNBConsecutiveBlocks"
								placeholder="NB of Consecutive blocks"
								disabled={!values.hasCurtailabilityForward}
								minRows={1}
								onChange={handleChange}
								variant="standard"
								type="number"
								value={
									values.curtailabilityForwardNBConsecutiveBlocks
								}
								error={
									!!errors.curtailabilityForwardNBConsecutiveBlocks
								}
								errorText={
									errors.curtailabilityForwardNBConsecutiveBlocks
								}
								touched={true}
							/>
						</FormControl>
					</Grid>
				</If>
			</Feature>
			<If
				condition={
					stepConfig.show_pricing_section_cap_floor ||
					stepConfig.show_pricing_section_cap_floor_intraday
				}
			>
				<CapFloorCard
					values={values}
					handleChange={handleChange}
					setFieldValue={setFieldValue}
					errors={errors}
					gridSize={3}
					wrap={false}
					showCapFloor={stepConfig.show_pricing_section_cap_floor}
					showCapFloorIntraday={
						stepConfig.show_pricing_section_cap_floor_intraday
					}
				/>
			</If>
			{tender?.countries.includes(SupportedCountry.UNITED_STATES) && (
				<Grid
					item
					xs={3}
					display="flex"
					flexDirection={"column"}
					justifyContent="flex-start"
				>
					<FormGroup>
						<FormControlLabel
							control={
								<Switch
									name={"isNodal"}
									checked={values.isNodal}
									onChange={handleChange}
								/>
							}
							label="Is nodal"
						/>
					</FormGroup>
				</Grid>
			)}
			<Grid item xs={12} display="flex" justifyContent="flex-start">
				<FormGroup>
					<FormControlLabel
						control={
							<Switch
								name={"applySetupFees"}
								checked={values.applySetupFees}
								onChange={handleChange}
							/>
						}
						label="Apply Setup Fees"
					/>
				</FormGroup>
			</Grid>
			<Grid
				item
				xs={12}
				display="flex"
				justifyContent="flex-start"
				direction="column"
			>
				<UsSpecificsSelectors
					setFieldValue={setFieldValue}
					values={values}
					spotMarketsList={spotMarketsList}
					provisionTypesList={provisionTypesList}
					tender={tender}
				/>
			</Grid>
			<If
				condition={
					stepConfig.show_pricing_section_addons &&
					isFeature(FeatureName.enable_pricing_addons_sync, true)
				}
			>
				<Grid item xs={12} display="flex" justifyContent="flex-start">
					<FormGroup>
						<FormLabel>Pricing Addons</FormLabel>
						{addonsList.map((addon) => (
							<FormControlLabel
								key={addon.id}
								control={
									<Checkbox
										checked={values.pricing_add_ons?.some(
											(a) => a.geco_id === addon.geco_id
										)}
									/>
								}
								label={addon.name}
								onChange={(event) =>
									setFieldValue(
										"pricing_add_ons",
										_.xorBy(
											values.pricing_add_ons,
											[addon],
											"geco_id"
										)
									)
								}
							/>
						))}
					</FormGroup>
				</Grid>
			</If>
			<If condition={stepConfig.available_ppa_types?.length > 1}>
				<Grid
					item
					xs={4}
					display="flex"
					justifyContent="flex-start"
					direction="column"
				>
					<FluidSelectMultiple
						items={(stepConfig.available_ppa_types || []).map(
							(type) => ({
								label: type,
								value: type,
							})
						)}
						label={"PPA shape Types"}
						name={"availablePpaTypes"}
						value={values.ppaShapeType}
						onChange={(value) =>
							setFieldValue("ppaShapeType", value)
						}
					/>
					<If condition={values.pricingType?.length > 1}>
						<Alert severity="warning">
							This might not apply to each pricing types
						</Alert>
					</If>
				</Grid>
			</If>
		</Grid>
	);
}

export default PricingStep;
